import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const QUERY_PARAM = 'query';
const PAGE_PARAM = 'page';

export interface SearchState {
  query: string;
  page: number;
  setQuery: (query: string) => void;
  setPage: (page: number) => void;
  reset: () => void;
}

/**
 * URL-driven search state — the back/forward buttons must work and any search
 * URL must be shareable, so the URL is the source of truth (DESIGN.md §13).
 */
export function useSearchState(): SearchState {
  const [params, setParams] = useSearchParams();

  const query = params.get(QUERY_PARAM)?.trim() ?? '';
  const pageRaw = Number(params.get(PAGE_PARAM));
  const page = Number.isFinite(pageRaw) && pageRaw > 0 ? Math.floor(pageRaw) : 1;

  const setQuery = useCallback(
    (next: string) => {
      setParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          const trimmed = next.trim();
          if (trimmed) {
            out.set(QUERY_PARAM, trimmed);
          } else {
            out.delete(QUERY_PARAM);
          }
          // Any new search resets pagination.
          out.delete(PAGE_PARAM);
          return out;
        },
        { replace: false },
      );
    },
    [setParams],
  );

  const setPage = useCallback(
    (next: number) => {
      setParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          if (next > 1) {
            out.set(PAGE_PARAM, String(next));
          } else {
            out.delete(PAGE_PARAM);
          }
          return out;
        },
        { replace: false },
      );
    },
    [setParams],
  );

  const reset = useCallback(() => {
    setParams(new URLSearchParams(), { replace: false });
  }, [setParams]);

  return useMemo(
    () => ({ query, page, setQuery, setPage, reset }),
    [query, page, setQuery, setPage, reset],
  );
}
